import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { TextField, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox, Grid, Typography, Alert } from '@mui/material';
import { z } from 'zod';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { stepValidator } from '@/components/wizard/validateWithZod';
import { useDataProvider } from 'react-admin';
import { EmployeeSearchAutocomplete } from '@/components/employee/EmployeeSearchAutocomplete';
import type { EmployeeSearchResult } from '@/components/employee';
import { usePriorContext } from '@/components/employee/usePriorContext';
import { useRuleEvaluation } from '@/components/workflow';


interface PrefilledField {
  field: string;
  label: string;
  sourceValue: unknown;
  currentValue: unknown;
  confirmed: boolean;
}

const STEPS = [
  { id: 'employeeType', title: 'Employee & Type' },
  { id: 'validityPeriod', title: 'Validity Period' },
  { id: 'renewalAlerts', title: 'Renewal Alerts' },
  { id: 'reviewSubmit', title: 'Review & Submit' },
];

const CERTIFICATION_TYPES = [
  'First Aid Level 1',
  'First Aid Level 2',
  'First Aid Level 3',
  'Fire Safety',
  'Forklift License',
  'OHS Representative Certificate',
];

interface FormValues {
  employeeName: string;
  employeeId: string;
  certificationType: string;
  issuingBody: string;
  certifiedDate: string;
  expiryDate: string;
  certificateReference: string;
  alert30Days: boolean;
  additionalRecipients: string;
}

const INITIAL_VALUES: FormValues = {
  employeeName: '',
  employeeId: '',
  certificationType: '',
  issuingBody: '',
  certifiedDate: '',
  expiryDate: '',
  certificateReference: '',
  alert30Days: true,
  additionalRecipients: '',
};

const stepSchemas = {
  employeeType: z.object({
    employeeName: z.string().trim().min(1, 'Employee name is required'),
    employeeId: z.string().trim().min(1, 'Employee ID is required'),
    certificationType: z.string().min(1, 'Certification type is required'),
    issuingBody: z.string().trim().min(1, 'Issuing body is required'),
  }),
  validityPeriod: z.object({
    certifiedDate: z.string().min(1, 'Certification date is required'),
    expiryDate: z.string().min(1, 'Expiry date is required'),
  }),
};

const validateStep = (stepId: string, values: FormValues) => {
  const schema = stepSchemas[stepId as keyof typeof stepSchemas];
  if (!schema) return { isValid: true, errors: {} };
  const result = stepValidator(schema)(values as unknown as Record<string, unknown>);

  if (stepId === 'validityPeriod') {
    if (values.certifiedDate && values.expiryDate) {
      const certifiedDateObj = new Date(values.certifiedDate);
      const expiryDateObj = new Date(values.expiryDate);
      if (expiryDateObj <= certifiedDateObj) {
        result.errors.expiryDate = 'Expiry date must be after certification date';
        result.isValid = false;
      }
    }
    if (values.certifiedDate) {
      const certifiedDateObj = new Date(values.certifiedDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (certifiedDateObj > today) {
        result.errors.certifiedDate = 'Certification date cannot be in the future';
        result.isValid = false;
      }
    }
  }

  return result;
};

export const CertificationWizard = () => {
  const { employeeId } = useParams();
  const dataProvider = useDataProvider();

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearchResult | null>(null);
  const [prefilledFields, setPrefilledFields] = useState<PrefilledField[]>([]);

  const priorData = usePriorContext({ dataProvider, employeeId: selectedEmployee?.employeeNumber || '' });

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    const context: Record<string, unknown> = {
      certificationId: null,
      certificateType: v.certificationType,
      issuingBody: v.issuingBody,
      issuedDate: v.certifiedDate,
      expiryDate: v.expiryDate,
      certificateHolder: v.employeeName,
      reminderSent: false,
      reminderSentAt: null,
      renewalApplicationDate: null,
      restrictionActive: false,
    };

    const certResult = await dataProvider.create('ohs-certifications', { data: formValues });
    const certId = certResult.data.id;

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'ohs-certification',
        entityId: certId,
        entityName: 'Cert: ' + v.certificationType + ' - ' + v.employeeName,
        currentState: { value: 'Active', context },
        status: 'active',
        workflowVersion: '1.0',
        startedAt: new Date().toISOString(),
        lastActionAt: new Date().toISOString(),
        pausedAt: null,
        breakpointId: null,
        relatedEntityId: certId,
        transitions: [],
      },
    });

    return { id: certId };
  }, [dataProvider]);

  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    isSaving,
    isCompleted,
    progress,
    values,
    errors,
    stepFeedback,
    createdId,
    nextStep,
    prevStep,
    updateValues,
    validateStep: validateStepHook,
    complete,
    reset,
  } = useWizard({
    steps: STEPS,
    initialValues: { ...INITIAL_VALUES, employeeId: employeeId || '' },
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const { evaluate: evaluateRule, isEvaluating: isEvaluatingRule, result: ruleResult } = useRuleEvaluation({
    ruleId: 'ohs-certification-guards',
    context: {
      entityId: createdId || '',
      entityType: 'process',
      currentState: currentStep?.id || 'initiated',
      event: 'SUBMIT',
      certificationType: (values as unknown as FormValues).certificationType,
      employeeId: (values as unknown as FormValues).employeeId,
    },
  });

  useEffect(() => {
    if (currentStep?.id === 'reviewSubmit') {
      evaluateRule();
    }
  }, [currentStep?.id, evaluateRule]);

  const valuesTyped = values as unknown as FormValues;

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value as unknown });
  };

  const handleEmployeeSelect = (employee: EmployeeSearchResult) => {
    setSelectedEmployee(employee);
    const fields: PrefilledField[] = [
      {
        field: 'department',
        label: 'Department',
        sourceValue: employee.organisationalHierarchy?.department || 'Not available',
        currentValue: '',
        confirmed: false,
      },
      {
        field: 'position',
        label: 'Position',
        sourceValue: employee.seniority?.jobTitle || 'Not available',
        currentValue: '',
        confirmed: false,
      },
    ];
    setPrefilledFields(fields);
    updateValues({
      employeeName: employee.fullName,
      employeeId: employee.employeeNumber,
    });
  };

  const handleClearEmployee = () => {
    setSelectedEmployee(null);
    setPrefilledFields([]);
    updateValues({
      employeeName: '',
      employeeId: '',
    });
  };

  const calculateValidityMonths = (): number => {
    if (!valuesTyped.certifiedDate || !valuesTyped.expiryDate) return 0;
    const certified = new Date(valuesTyped.certifiedDate);
    const expiry = new Date(valuesTyped.expiryDate);
    const months = (expiry.getFullYear() - certified.getFullYear()) * 12 + (expiry.getMonth() - certified.getMonth());
    return months;
  };

  const handleNext = () => {
    const result = validateStep(currentStep.id, valuesTyped);
    if (!result.isValid) {
      return;
    }

    if (currentStep.id === 'validityPeriod') {
      const months = calculateValidityMonths();
    }

    if (currentStep.id === 'renewalAlerts') {
      // noop
    }

    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const handleBack = () => {
    prevStep();
  };

  if (isCompleted) {
    return (
      <WizardLayout
        steps={STEPS}
        currentStepIndex={currentStepIndex}
        progress={progress}
        isSaving={isSaving}
        isCompleted={isCompleted}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        onReset={reset}
      >
        <OutcomeSummary
          title="Certification Recorded"
          processId={createdId || undefined}
          fields={[
            { label: 'Employee', value: valuesTyped.employeeName },
            { label: 'Employee #', value: valuesTyped.employeeId },
            { label: 'Certification Type', value: valuesTyped.certificationType },
            { label: 'Issuing Body', value: valuesTyped.issuingBody },
            { label: 'Certified Date', value: valuesTyped.certifiedDate },
            { label: 'Expiry Date', value: valuesTyped.expiryDate },
            { label: 'Certificate Reference', value: valuesTyped.certificateReference },
            { label: '30-Day Alert', value: valuesTyped.alert30Days ? 'Enabled' : 'Disabled' },
          ]}
          footerActions={[
            { label: 'View Certifications', to: '/ohs-certifications' },
            { label: 'Add Another', onClick: reset },
          ]}
        />
      </WizardLayout>
    );
  }

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={handleBack}
      onNext={handleNext}
      onComplete={complete}
      onReset={reset}
    >
      {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5, color: 'text.secondary' }}>
            Step {currentStepIndex + 1} of {totalSteps}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            {currentStep.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {getStepDescription(currentStep.id)}
          </Typography>
        </Grid>
      </Grid>

      {renderFormFields(currentStep.id, valuesTyped, errors, updateField, calculateValidityMonths, selectedEmployee, prefilledFields, priorData, handleEmployeeSelect, handleClearEmployee, ruleResult)}
    </WizardLayout>
  );
};

function getStepDescription(stepId: string): string {
  const descriptions: Record<string, string> = {
    employeeType: 'Select the employee and type of OHS certification',
    validityPeriod: 'Enter the certification validity dates',
    renewalAlerts: 'Configure automated renewal alert notifications',
    reviewSubmit: 'Review and submit the certification record',
  };
  return descriptions[stepId] || '';
}

function renderFormFields(
  stepId: string,
  values: FormValues,
  errors: Record<string, string>,
  updateValues: (field: keyof FormValues, value: unknown) => void,
  calculateValidityMonths: () => number,
  selectedEmployee: EmployeeSearchResult | null,
  prefilledFields: PrefilledField[],
  priorData: { priorCertifications: Array<{ id: string; employeeId: string; certificationType: string; status: string; certifiedAt: string; expiryDate: string }> },
  onEmployeeSelect: (employee: EmployeeSearchResult) => void,
  onClearEmployee: () => void,
  ruleResult: { allowed: boolean; reason?: string } | null,
): React.ReactNode {
  switch (stepId) {
    case 'employeeType':
      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <EmployeeSearchAutocomplete
              onEmployeeSelect={onEmployeeSelect}
              onClear={onClearEmployee}
            />
          </Grid>
          {prefilledFields.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Employee Info:</strong> {selectedEmployee?.fullName} ({selectedEmployee?.employeeNumber})
                </Typography>
                {prefilledFields.map(field => (
                  <Typography key={field.field} variant="body2">
                    {field.label}: {String(field.sourceValue)}
                  </Typography>
                ))}
              </Alert>
            </Grid>
          )}
          {priorData.priorCertifications.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                This employee has {priorData.priorCertifications.length} existing certification(s)
              </Alert>
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth error={!!errors.certificationType}>
              <InputLabel>Certification Type</InputLabel>
              <Select
                value={values.certificationType}
                label="Certification Type"
                onChange={e => updateValues('certificationType', e.target.value)}
              >
                {CERTIFICATION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            {errors.certificationType && <Typography variant="caption" color="error">{errors.certificationType}</Typography>}
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Issuing Body"
              value={values.issuingBody}
              onChange={e => updateValues('issuingBody', e.target.value)}
              fullWidth
              error={!!errors.issuingBody}
              helperText={errors.issuingBody || 'e.g., Red Cross, SAQA, NOSA'}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Alert severity="info">
              Ensure the certification type matches the relevant OHS Act requirements.
              First Aid certifications must meet SAQA accreditation standards.
            </Alert>
          </Grid>
        </Grid>
      );

    case 'validityPeriod':
      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Date Certified"
              type="date"
              value={values.certifiedDate}
              onChange={e => updateValues('certifiedDate', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.certifiedDate}
              helperText={errors.certifiedDate || 'Date certification was issued'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Expiry Date"
              type="date"
              value={values.expiryDate}
              onChange={e => updateValues('expiryDate', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.expiryDate}
              helperText={errors.expiryDate || 'Date certification expires'}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Certificate Reference Number"
              value={values.certificateReference}
              onChange={e => updateValues('certificateReference', e.target.value)}
              fullWidth
              placeholder="e.g., RCC/2024/001234"
            />
          </Grid>
          {values.certifiedDate && values.expiryDate && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="success">
                Certification validity: {calculateValidityMonths()} months
              </Alert>
            </Grid>
          )}
        </Grid>
      );

    case 'renewalAlerts':
      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.alert30Days}
                  onChange={e => updateValues('alert30Days', e.target.checked)}
                />
              }
              label="Enable 30-day renewal alert"
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
              Send notification 30 days before certificate expires
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Additional Alert Recipients"
              value={values.additionalRecipients}
              onChange={e => updateValues('additionalRecipients', e.target.value)}
              fullWidth
              placeholder="email1@example.com, email2@example.com"
              helperText="Comma-separated email addresses (optional)"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Alert severity="info">
              Renewal alerts will be sent to the employee, their manager, and any additional recipients specified.
              Alerts are triggered30 days before expiry to allow sufficient time for renewal processing.
            </Alert>
          </Grid>
        </Grid>
      );

    case 'reviewSubmit':
      return (
        <Grid container spacing={2}>
          {ruleResult && !ruleResult.allowed && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Guard Check</Typography>
                <Typography variant="body2">{ruleResult.reason}</Typography>
              </Alert>
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">Employee</Typography>
                <Typography variant="body1">{values.employeeName || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">Employee ID</Typography>
                <Typography variant="body1">{values.employeeId || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">Certification Type</Typography>
                <Typography variant="body1">{values.certificationType || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">Issuing Body</Typography>
                <Typography variant="body1">{values.issuingBody || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">Certified Date</Typography>
                <Typography variant="body1">{values.certifiedDate || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">Expiry Date</Typography>
                <Typography variant="body1">{values.expiryDate || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">Certificate Reference</Typography>
                <Typography variant="body1">{values.certificateReference || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">30-Day Alert</Typography>
                <Typography variant="body1">{values.alert30Days ? 'Enabled' : 'Disabled'}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Alert severity="success">
              Certification record is ready to be submitted. The record will be stored and
              renewal alerts will be active as configured.
            </Alert>
          </Grid>
        </Grid>
      );

    default:
      return null;
  }
}